import { StrictMode, lazy, Suspense, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { trackPageView } from './services/analytics';
import './index.css';

// Homepage imported statically for fast First Contentful Paint (FCP)
import App from './App.tsx';
import Nav from './components/Nav.tsx';

// Lazy-load other routes to reduce initial JavaScript bundle size (Lighthouse optimization)
const Blog = lazy(() => import('./pages/Blog.tsx'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy.tsx'));
const Terms = lazy(() => import('./pages/Terms.tsx'));
const CommandCenter = lazy(() => import('./pages/CommandCenter.tsx'));
const AppsDirectory = lazy(() => import('./pages/AppsDirectory.tsx'));
const ArtisanFlow = lazy(() => import('./pages/ArtisanFlow.tsx'));
const QRSafe = lazy(() => import('./pages/QRSafe.tsx'));
const RequestBuild = lazy(() => import('./pages/RequestBuild.tsx'));

// Lazy load global 3D canvas
const HeroCanvas = lazy(() => import('./components/HeroCanvas'));

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

const GlobalBackground = () => {
  const [shouldRenderCanvas, setShouldRenderCanvas] = useState(false);

  useEffect(() => {
    // Check if mobile viewport (e.g. width < 768px)
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // On mobile, delay the rendering of the heavy 3D canvas by 1.5 seconds
      // to let the critical text layout paint first without CPU blocking
      const timer = setTimeout(() => {
        setShouldRenderCanvas(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // On desktop, initialize immediately
      setShouldRenderCanvas(true);
    }
  }, []);

  return (
    <div className="canvas-container-fixed">
      {shouldRenderCanvas ? (
        <Suspense fallback={null}>
          <HeroCanvas scrollContainerRef={{ current: typeof document !== 'undefined' ? document.body : null }} />
        </Suspense>
      ) : (
        /* Static luxury dark-mode background fallback during early paint */
        <div style={{
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at center, #0d0d12 0%, #050507 100%)',
          position: 'absolute',
          top: 0,
          left: 0
        }} />
      )}
    </div>
  );
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AnalyticsTracker />
      {/* Global 3D canvas behind all pages */}
      <GlobalBackground />
      {/* Global navigation — renders on every page */}
      <Nav />
      <div style={{ position: 'relative', zIndex: 2 }}>
        <Suspense fallback={
          <div className="route-loading-fallback" style={{ 
            minHeight: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center', 
            justifyContent: 'center', 
            background: '#050507', 
            color: '#7ca8b0', 
            fontFamily: 'var(--font-mono)', 
            fontSize: '0.7rem', 
            letterSpacing: '0.25em',
            textTransform: 'uppercase'
          }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid rgba(124,168,176,0.1)', borderTopColor: '#7ca8b0', animation: 'spin 1s linear infinite' }} />
            Creating Seamless Tunnel...
          </div>
        }>
          <Routes>
            <Route path="/" element={<App />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            {/* ── Password-protected admin route ────────────────────────── */}
            <Route path="/command-center" element={<CommandCenter />} />
            <Route path="/apps" element={<AppsDirectory />} />
            <Route path="/artisanflow" element={<ArtisanFlow />} />
            <Route path="/qrsafe" element={<QRSafe />} />
            <Route path="/request-build" element={<RequestBuild />} />
            {/* Catch-all: redirect unknown routes to home */}
            <Route path="*" element={<App />} />
          </Routes>
        </Suspense>
      </div>
    </BrowserRouter>
  </StrictMode>,
);
